import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import SimpleReactValidator from 'simple-react-validator';
import { isNationalId, isNationalCode } from 'persianhelper';

import CounterDown from '../assets/CountDown';
import BtnLoader from '../assets/loaders/BtnLoader';

import { GetStates } from '../../store/actions/kalapost';
import { verifyUserLogin, verifyUserRegistered } from '../../store/actions/user';
import {
   userLoginStart,
   userRegisterCode,
   userRegisterComplete,
   userRegistered,
   userRegisterPhone,
} from '../../store/actions/authorize';
import { forgetPasswordService, GetDesCitiesService } from '../../services/registerApi';
import { getNationalStatusService } from '../../services/userService';
import { errorMessage, successMessage } from '../../utils/message';
import { canTypeForNumInput } from '../../utils/numbers';
import { decodeToken } from '../../utils/decodeToken';
import { toEnglishNumbers } from '../../utils/datePicker';
import {
   correctCharacters,
   isAddress,
   isFullName,
   isNumber,
   isPhoneNumber,
} from '../../utils/persianLatter';

const btnStyle = {
   minHeight: '44px',
   display: 'flex',
   alignItems: 'center',
   justifyContent: 'center',
   gap: '6px',
   marginTop: '44px',
};
const errorMsgStype = {
   color: '#ff0000',
   margin: '6px 6px 0 0',
   minHeight: '20px',
   position: 'absolute',
   top: '3.7rem',
   fontSize: '1.1rem',
};

let timeout;

const Login = () => {
   const location = useLocation();
   const navigate = useNavigate();
   const dispatch = useDispatch();
   const { executeRecaptcha } = useGoogleReCaptcha();

   const [, forceUpdate] = useState();
   const [isUniqueNationalId, setIsUniqueNationalId] = useState(true);
   const [step, setStep] = useState('phone'); // phone, pass, smsCode, setPass, info, forgetSmsCode, setNewPass
   const [btnLoading, setBtnLoading] = useState(false);
   const [phone, setPhone] = useState('');
   const [tokenCode, setTokenCode] = useState('');
   const [codeMessage, setCodeMessage] = useState('');
   const [millis, setMillis] = useState('');
   const [legal, setLegal] = useState(false);
   const [login, setLogin] = useState({
      address: '',
      postalCode: '',
      nationalId: '',
      cityId: '',
      fullname: '',
      code: '',
      password: '',
      repPassword: '',
      isState: false,
      stateId: '',
      states: [],
      cites: [],
   });

   const validator = useRef(
      new SimpleReactValidator({
         messages: {
            required: 'پر کردن این فیلد الزامی می‌باشد',
         },
         validators: {
            isNationalIdValid: {
               message: 'شناسه ملی واردشده معتبر نمی‌باشد',
               rule: (val) => {
                  return isNationalId(val);
               },
               required: true,
            },
            isNationalCodeValid: {
               message: 'کدملی واردشده معتبر نمی‌باشد',
               rule: (val) => {
                  return isNationalCode(val);
               },
               required: true,
            },
            phoneNum: {
               message: 'شماره واردشده معتبر نمی‌باشد',
               rule: (val) => {
                  const numberStatus = isNumber(val);
                  const charStatus = numberStatus && correctCharacters(val);
                  const validPhone = isPhoneNumber(charStatus);
                  return validPhone;
               },
               required: true,
            },
            addressValid: {
               message: 'آدرس واردشده معتبر نمی‌باشد',
               rule: (val) => {
                  const add = isAddress(val);
                  return add;
               },
               required: true,
            },
            inputLength: {
               message: 'نام و نام خانوادگی واردشده معتبر نمی‌باشد',
               rule: (val) => {
                  const name = isFullName(val);
                  return name;
               },
               required: true,
            },
            postalValid: {
               message: 'کد پستی واردشده معتبر نمی‌باشد',

               rule: (val) => {
                  const regpostalCode = new RegExp(`^([0-9]{10})$`);
                  return regpostalCode.test(val);
               },
               required: true, // optional
            },
            validConfirmPass: {
               message: 'رمز عبور و تکرار آن یکسان نیست',
               rule: () => {
                  return login.password === login.repPassword;
               },
            },
            passwordLen: {
               message: 'رمز عبور باید حداقل شامل 8 کاراکتر باشد',
               rule: (val, params, validator) => val.length >= 8,
               required: true,
            },
            validPassword: {
               message: 'رمز عبور باید ترکیبی از حروف بزرگ، کوچک، اعداد و علائم باشد',
               rule: (val, params, validator) => {
                  return val.length > 0
                     ? validator.helpers.testRegex(
                          val,
                          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#$@!%&*?])[A-Za-z\d#$@!%&*?]{8,30}$/
                       ) && params.indexOf(val) === -1
                     : null;
               },
               required: true,
            },
         },
         element: (message) => <div style={errorMsgStype}>{message}</div>,
      })
   );

   const handleReCaptchaVerify = useCallback(async () => {
      if (!executeRecaptcha) {
         return;
      }
      const token = await executeRecaptcha();
      return token;
   }, [executeRecaptcha]);

   useEffect(() => {
      timeout = setTimeout(async () => {
         if (
            login.nationalId &&
            ((legal && isNationalId(login.nationalId)) ||
               (!legal && isNationalCode(login.nationalId)))
         ) {
            const res = await getNationalStatusService(login.nationalId);
            if (res?.data?.success) {
               validator.current.showMessageFor('nationalId');
               setIsUniqueNationalId(false);
               errorMessage('کدملی تکراری است');
            }
         }
      }, 700);
      setIsUniqueNationalId(true);
      return () => clearTimeout(timeout);
   }, [login.nationalId, legal]);
   useEffect(() => {
      validator.current.hideMessages();
   }, [step]);

   const handleChange = (e) => {
      setLogin({ ...login, [e.target.name]: toEnglishNumbers(e.target.value) });
      validator.current.showMessageFor(e.target.name);
   };
   const handleChangeState = async (e) => {
      const { status, data } = await GetDesCitiesService(e.target.value);
      if (status) {
         setLogin((prev) => ({
            ...prev,
            stateId: e.target.value,
            cites: data,
            isState: true,
         }));
      } else {
         setLogin((prev) => ({ ...prev, stateId: e.target.value }));
      }
   };
   const renderForm = () => {
      if (step === 'phone')
         return (
            <>
               <h2 className='login__content-title'>ورود | ثبت نام</h2>
               <form onSubmit={handleSubmitPhone} style={{ width: '100%' }}>
                  <p className='login__content-text'>لطفا شماره تلفن همراه خود را وارد کنید</p>
                  <label htmlFor='login__content-section' className='login__content-section'>
                     <input
                        type='text'
                        className='login__content-input'
                        style={{ direction: 'ltr' }}
                        placeholder='تلفن همراه'
                        name='phone'
                        value={phone}
                        onChange={(e) => setPhone(toEnglishNumbers(e.target.value))}
                        onKeyDown={canTypeForNumInput}
                     />
                     <span className='login__content-focus'></span>
                     {validator.current.message('phone', phone, 'required|phoneNum')}
                  </label>
                  <button
                     type='submit'
                     className='login__content-btn'
                     style={{
                        ...btnStyle,
                        ...(btnLoading ? { background: '#ccc !important' } : {}),
                     }}
                  >
                     تایید
                     {btnLoading && (
                        <BtnLoader
                           style={{
                              width: '15px',
                              height: '15px',
                              marginBottom: '2px',
                           }}
                        />
                     )}
                  </button>
               </form>
            </>
         );
      if (step === 'pass')
         return (
            <>
               <h2 className='login__content-title'>ورود به کالاپست</h2>
               <form
                  onSubmit={handleLogin}
                  style={{
                     width: '100%',
                     display: 'flex',
                     flexDirection: 'column',
                     justifyContent: 'center',
                  }}
               >
                  <p className='login__content-text'>لطفا رمز عبور خود را وارد نمایید</p>
                  <label
                     htmlFor='login__content-section'
                     className='login__content-section'
                     style={{ marginBottom: '5px' }}
                  >
                     <input
                        type='password'
                        className='login__content-input'
                        name='password'
                        placeholder='رمز عبور'
                        value={login.password}
                        onChange={handleChange}
                     />
                     <span className='login__content-focus'></span>
                     {validator.current.message(
                        'password',
                        login.password,
                        'required|passwordLen|validPassword'
                     )}
                  </label>
                  <button className='login__content-btn' type='submit' style={btnStyle}>
                     ورود
                     {btnLoading && (
                        <BtnLoader
                           style={{
                              width: '15px',
                              height: '15px',
                              marginBottom: '2px',
                           }}
                        />
                     )}
                  </button>
                  <p className='login__forget-pass' onClick={handleClickForgetPass}>
                     رمز عبور خود را فراموش کرده اید؟
                  </p>
               </form>
            </>
         );
      if (step === 'smsCode' || step === 'forgetSmsCode')
         return (
            <>
               <h2 className='login__content-title'>
                  {step === 'smsCode' ? 'ثبت نام در کالاپست' : 'ورود به کالاپست'}
               </h2>
               <form onSubmit={handleSubmitCode} style={{ width: '100%' }}>
                  <p className='login__content-text' style={{ paddingBottom: '8px' }}>
                     لطفا کد ارسالی به شماره تلفن همراه خود را وارد کنید
                  </p>{' '}
                  <CounterDown
                     millis={millis}
                     phone={phone}
                     reSend={(data) => {
                        successMessage(data.message);
                        setTokenCode(data.tokenCode);
                        const { date } = decodeToken(data.tokenCode);
                        setMillis(date - Date.now());
                     }}
                  />
                  <label htmlFor='login__content-section' className='login__content-section'>
                     <input
                        type='text'
                        placeholder='&nbsp;'
                        className='login__content-input'
                        style={{ direction: 'ltr' }}
                        name='code'
                        value={login.code}
                        onChange={handleChange}
                        onKeyDown={canTypeForNumInput}
                     />
                     <span className='login__content-focus'></span>
                  </label>
                  <button style={btnStyle} className='login__content-btn' type='submit'>
                     تایید و ادامه
                     {btnLoading && (
                        <BtnLoader
                           style={{
                              width: '15px',
                              height: '15px',
                              marginBottom: '2px',
                           }}
                        />
                     )}
                  </button>
               </form>
            </>
         );
      if (step === 'setPass' || step === 'setNewPass')
         return (
            <>
               <h2 className='login__content-title'>
                  {step === 'setPass' ? 'ثبت نام در کالاپست' : 'بازیابی رمز عبور'}
               </h2>
               <form onSubmit={handlCreatePassword} style={{ width: '100%' }}>
                  <label htmlFor='login__content-section' className='login__content-section'>
                     <input
                        type='password'
                        className='login__content-input'
                        placeholder='&nbsp;'
                        name='password'
                        value={login.password}
                        onChange={handleChange}
                     />
                     <span className='login__content-label'>رمز عبور</span>
                     <span className='login__content-focus'></span>
                     {validator.current.message(
                        'password',
                        login.password,
                        'required|passwordLen|validPassword'
                     )}
                  </label>
                  <label htmlFor='login__content-section' className='login__content-section '>
                     <input
                        placeholder='&nbsp;'
                        // placeholder={"تکرار رمزعبور"}
                        className='login__content-input '
                        type='password'
                        name='repPassword'
                        onChange={handleChange}
                        value={login.repPassword}
                     />
                     <span className='login__content-label'>تکرار رمز عبور</span>
                     <span className='login__content-focus'></span>
                     {validator.current.message(
                        'required|passwordLen|validPassword',
                        login.repPassword,
                        `in:${login.password}`,
                        {
                           messages: {
                              in: 'رمز عبور و تکرار رمز یکسان نیست',
                           },
                        }
                     )}
                  </label>
                  <button className='login__content-btn' type='submit' style={btnStyle}>
                     تایید و ادامه
                     {btnLoading && (
                        <BtnLoader
                           style={{
                              width: '15px',
                              height: '15px',
                              marginBottom: '2px',
                           }}
                        />
                     )}
                  </button>
               </form>
            </>
         );
      if (step === 'info')
         return (
            <>
               <div
                  style={{
                     width: '100%',
                     display: 'flex',
                     textAlign: 'center',
                     fontSize: '1.3rem',
                     fontWeight: 'bold',
                     cursor: 'pointer',
                     paddingBottom: '30px',
                  }}
               >
                  <div
                     style={{
                        width: '50%',
                        padding: '8px 16px',
                        border: '2px solid var(--primary)',
                        borderRadius: '0 8px 8px 0',
                        borderLeft: 'none',
                        ...(legal === false ? { background: 'var(--primary)', color: '#fff' } : {}),
                     }}
                     onClick={() => setLegal(false)}
                  >
                     حقیقی
                  </div>
                  <div
                     style={{
                        width: '50%',
                        padding: '8px 16px',
                        border: '2px solid var(--primary)',
                        borderRadius: '8px 0 0 8px',
                        borderRight: 'none',
                        ...(legal === true ? { background: 'var(--primary)', color: '#fff' } : {}),
                     }}
                     onClick={() => setLegal(true)}
                  >
                     حقوقی
                  </div>
               </div>
               {/* <h2 className="login__content-title">ثبت نام</h2> */}
               <form
                  className='login__content-form'
                  style={{ gap: '16px 20px' }}
                  onSubmit={handleRegister}
               >
                  {/* fullname */}
                  <label
                     htmlFor='login__content-section'
                     className='login__content-section login__content-section--complete'
                  >
                     <input
                        autoFocus
                        type='text'
                        className='login__content-input login__content-input--complete'
                        placeholder='&nbsp;'
                        name='fullname'
                        value={login.fullname}
                        onChange={handleChange}
                        onBlur={(e) => validator.current.showMessageFor(e.target.name)}
                     />
                     <span className='login__content-label'>
                        {legal ? 'نام شرکت' : 'نام و نام خانوادگی'}
                     </span>
                     <span className='login__content-focus'></span>
                     {validator.current.message('fullname', login.fullname, 'required|inputLength')}
                  </label>
                  {/* phoneNumber */}
                  <label
                     htmlFor='login__content-section'
                     className='login__content-section login__content-section--complete'
                  >
                     <input
                        type='text'
                        className='login__content-input login__content-input--complete'
                        placeholder='&nbsp;'
                        name='phone'
                        value={phone}
                        onKeyDown={canTypeForNumInput}
                        onChange={(e) => setPhone(e.target.value)}
                        readOnly
                        disabled
                     />
                     <span className='login__content-label'>شماره تلفن همراه</span>
                     <span className='login__content-focus'></span>
                     {validator.current.message('phone', phone, 'required|phoneNum')}
                  </label>
                  {/* state */}
                  <label
                     htmlFor='login__content-section'
                     className='login__content-section login__content-section--complete'
                  >
                     <select
                        name='stateId'
                        id='type-select'
                        className='login__content-input login__content-input--complete'
                        onChange={handleChangeState}
                        onBlur={(e) => validator.current.showMessageFor(e.target.name)}
                     >
                        <option hidden>انتخاب کنید ...</option>
                        {login.states.map((item, index) => (
                           <option key={index} value={item.stateId}>
                              {item.name}
                           </option>
                        ))}
                     </select>
                     <span className='login__content-label'>استان</span>
                     <span className='login__content-focus'></span>

                     {validator.current.message('stateId', login.stateId, 'required')}
                  </label>
                  {/* city */}
                  <label
                     htmlFor='login__content-section'
                     className='login__content-section login__content-section--complete'
                  >
                     <select
                        name='cityId'
                        id='type-select2'
                        className='login__content-input login__content-input--complete'
                        disabled={login.isState ? false : true}
                        value={login.cityId}
                        onChange={handleChange}
                        onBlur={(e) => validator.current.showMessageFor(e.target.name)}
                     >
                        <option hidden>انتخاب کنید ...</option>
                        {login.cites.map((item, index) => (
                           <option key={index} value={item.id}>
                              {item.name}
                           </option>
                        ))}
                     </select>
                     <span className='login__content-label'>شهر</span>
                     <span className='login__content-focus'></span>
                     {validator.current.message('cityId', login.cityId, 'required')}
                  </label>
                  {/* nationalId */}
                  <label
                     htmlFor='login__content-section'
                     className='login__content-section login__content-section--complete'
                  >
                     <input
                        type='text'
                        className='login__content-input login__content-input--complete'
                        placeholder='&nbsp;'
                        name='nationalId'
                        value={login.nationalId}
                        onChange={handleChange}
                        onKeyDown={canTypeForNumInput}
                        onBlur={(e) => validator.current.showMessageFor(e.target.name)}
                     />
                     <span className='login__content-label'>{legal ? 'شناسه ملی' : 'کد ملی'}</span>
                     <span className='login__content-focus'></span>
                     {isUniqueNationalId ? (
                        validator.current.message(
                           'nationalId',
                           login.nationalId,
                           `required|${legal ? 'isNationalIdValid' : 'isNationalCodeValid'}`
                        )
                     ) : (
                        <div style={errorMsgStype}>
                           {`${legal ? 'شناسه ملی' : 'کدملی'} واردشده تکراری می‌باشد`}
                        </div>
                     )}
                  </label>
                  {/* postalCode */}
                  <label
                     htmlFor='login__content-section'
                     className='login__content-section login__content-section--complete'
                  >
                     <input
                        type='text'
                        className='login__content-input login__content-input--complete'
                        placeholder='&nbsp;'
                        name='postalCode'
                        value={login.postalCode}
                        onChange={handleChange}
                        onKeyDown={canTypeForNumInput}
                        onBlur={(e) => validator.current.showMessageFor(e.target.name)}
                     />
                     <span className='login__content-label'>کدپستی</span>
                     <span className='login__content-focus'></span>
                     {validator.current.message(
                        'postalCode',
                        login.postalCode,
                        'required|postalValid'
                     )}
                  </label>
                  {/* address */}
                  <label
                     htmlFor='login__content-section'
                     className='login__content-section login__content-section--address'
                     style={{ marginBottom: '1.5rem' }}
                  >
                     <input
                        type='text'
                        className='login__content-input login__content-input--address'
                        placeholder='&nbsp;'
                        name='address'
                        value={login.address}
                        onChange={handleChange}
                        onBlur={(e) => validator.current.showMessageFor(e.target.name)}
                     />
                     <span className='login__content-label'>آدرس</span>
                     <span className='login__content-focus'></span>

                     {validator.current.message('address', login.address, 'required|addressValid')}
                  </label>
                  <div
                     style={{
                        flex: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        marginTop: '3rem',
                     }}
                  >
                     <button
                        className='login__content-btn'
                        type='submit'
                        style={{ ...btnStyle, marginTop: '0.5rem' }}
                     >
                        ثبت نام
                        {btnLoading && (
                           <BtnLoader
                              style={{
                                 width: '15px',
                                 height: '15px',
                                 marginBottom: '2px',
                              }}
                           />
                        )}
                     </button>
                  </div>
               </form>
            </>
         );
   };

   const handleSubmitPhone = async (e) => {
      e.preventDefault();
      if (btnLoading) return;

      if (validator.current.allValid()) {
         setBtnLoading(true);
         let token = await handleReCaptchaVerify();
         const { status } = await dispatch(userRegistered(phone, token));

         if (status) {
            setBtnLoading(false);
            setStep('pass');
            dispatch(verifyUserRegistered());
            validator.current.hideMessages();
         } else {
            token = await handleReCaptchaVerify();
            const res = await dispatch(userRegisterPhone(phone, token));
            setBtnLoading(false);

            if (res?.data?.success) {
               successMessage(res.data.message);
               setTokenCode(res.data.tokenCode);
               setStep('smsCode');
               const { date } = decodeToken(res.data.tokenCode);
               setMillis(date - Date.now());
            }
         }
      } else {
         validator.current.showMessages();
         forceUpdate(1);
      }
   };
   const handleLogin = async (e) => {
      e.preventDefault();
      if (btnLoading) return;

      if (validator.current.allValid()) {
         setBtnLoading(true);
         let token = await handleReCaptchaVerify();
         const result = await dispatch(userLoginStart(phone, login.password, token));
         setBtnLoading(false);

         if (result && location?.state?.from?.pathname) {
            navigate(location?.state?.from?.pathname);
         }
      } else {
         validator.current.showMessages();
         forceUpdate(1);
      }
   };
   const handleSubmitCode = async (e) => {
      e.preventDefault();
      if (btnLoading) return;
      if (!login.code) return errorMessage('لطفا کد ارسالی را وارد نمایید!');

      setBtnLoading(true);
      const token = await handleReCaptchaVerify();
      const res = await dispatch(userRegisterCode(phone, login.code, token, tokenCode));
      setBtnLoading(false);

      if (res) {
         setCodeMessage(login.code);
         setStep(step === 'smsCode' ? 'setPass' : 'setNewPass');
         validator.current.hideMessages();
      } else {
         // errorMessage('کد وارد شده اشتباه می‌باشد');
      }
   };
   const handlCreatePassword = async (e) => {
      e.preventDefault();
      if (btnLoading) return;
      if (login.password !== login.repPassword)
         return errorMessage('رمز عبور و تکرار آن یکسان نیست');
      // if (!validator.current.fieldValid('password')) return errorMessage('خطاها را رفع کنید');

      if (validator.current.allValid()) {
         setBtnLoading(true);
         if (step === 'setPass') {
            const res = await GetStates();
            if (res.status) {
               setStep('info');
               setLogin((prev) => ({ ...prev, states: res.data }));
               validator.current.hideMessages();
            }
         } else {
            const token = await handleReCaptchaVerify();
            const res = await forgetPasswordService(
               phone,
               login.password,
               login.code,
               tokenCode,
               token
            );
            if (!res.error) {
               dispatch(verifyUserLogin(res.data));
            }
         }
         setBtnLoading(false);
      } else {
         validator.current.showMessages();
         forceUpdate(1);
      }
   };
   const handleRegister = async (e) => {
      e.preventDefault();
      if (btnLoading) return;

      if (validator.current.allValid()) {
         setBtnLoading(true);
         const token = await handleReCaptchaVerify();
         const result = await dispatch(
            userRegisterComplete(
               phone,
               login.address,
               login.postalCode,
               login.nationalId,
               login.cityId,
               login.password,
               login.fullname,
               tokenCode,
               codeMessage,
               legal,
               token
            )
         );
         setBtnLoading(false);
         if (result && location?.state?.from?.pathname) {
            navigate(location?.state?.from?.pathname);
         }
      } else {
         validator.current.showMessages();
         forceUpdate(1);
      }
   };
   const handleClickForgetPass = async () => {
      if (btnLoading) return;

      setBtnLoading(true);
      const token = await handleReCaptchaVerify();
      const res = await dispatch(userRegisterPhone(phone, token));
      setBtnLoading(false);

      if (res?.data?.success) {
         setLogin((prev) => ({ ...prev, password: '' }));
         successMessage(res.data.message);
         setTokenCode(res.data.tokenCode);
         setStep('forgetSmsCode');
         const { date } = decodeToken(res.data.tokenCode);
         setMillis(date - Date.now());
      }
   };

   return (
      <div className='main__login'>
         <img src='/img/logo.png' alt='logo' className='login__content-img' />
         <div className='login__content' style={step === 'info' ? { maxWidth: '530px' } : {}}>
            {renderForm()}
         </div>
      </div>
   );
};

export default Login;
